import * as React from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import styled from 'styled-components';
import moment from 'moment';
import { useTranslation } from '@blocs.i18n';
import { media } from '@minecraft.themes';
import Icon from '@minecraft.icon';
import UncontrolledTooltip from '@minecraft.uncontrolled-tooltip';

/* ----- Styled Components ----- */
const StyledUncontrolledTooltip = styled(UncontrolledTooltip)`
  .tooltip-inner {
    a {
      text-decoration: underline;
      font-size: 0.75rem;
      color: ${(props) => props.theme.designSystem.textColors.inverted};
    }
  }
`;

const CalendarIcon = styled(Icon)<{ onClick: () => void }>`
  height: 1.25rem;
  width: 1.25rem;
  margin-left: 0.3125rem;
  cursor: pointer;
`;

const HelpIcon = styled(Icon)<{ id: string }>`
  width: 1rem;
  height: 1rem;
  margin-left: 0.3125rem;
  cursor: pointer;
`;

const DatePickerLabel = styled.label`
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const StyledDatePicker = styled(DatePicker)`
  height: 2.5rem;
  border: solid 1px ${(props) => props.theme.designSystem.borderColors.default};
  border-radius: 4px;
  padding: 0.25rem 0.5625rem 0;

  &:focus-visible {
    outline: ${(props) => props.theme.designSystem.borderColors.primary} auto 1px;
  }
`;

const CalendarIconWrapper = styled.div`
  display: flex;
  align-items: center;

  input {
    font-size: 1rem;
    margin-right: 0.25rem;
    width: 100%;

    ${media.sm`
      font-size: .875rem;
    `}
  }

  g {
    position: absolute;
  }

  & .react-datepicker-wrapper {
    flex-grow: 1;
  }

  & .react-datepicker__close-icon {
    right: 0.75rem;
  }

  .react-datepicker__close-icon::after {
    background-color: ${(props) => props.theme.designSystem.bgColors.card};
    color: ${(props) => props.theme.designSystem.textColors.default};
    font-size: 1.5rem;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: ${(props) => props.theme.designSystem.bgColors.lightContrast};
  }

  .react-datepicker__navigation-icon {
    top: 50%;
  }

  .react-datepicker__year-read-view--down-arrow {
    top: 20%;
  }

  .react-datepicker__month-read-view--down-arrow {
    top: 20%;
  }

  .react-datepicker__navigation--years::before {
    border-color: ${(props) => props.theme.designSystem.borderColors.emphasis};
    border-style: solid;
    border-width: 0.1875rem 0.1875rem 0 0;
    content: '';
    display: block;
    height: 0.5625rem;
    left: 0.6875rem;
    position: absolute;
    width: 0.5625rem;
  }

  .react-datepicker__navigation--years-upcoming::before {
    top: 1.0625rem;
    transform: rotate(315deg);
  }

  .react-datepicker__navigation--years-previous::before {
    top: 0.375rem;
    transform: rotate(135deg);
  }
`;

/* ----- Helpers ----- */
const DATE_FORMATS = {
  standard: 'MMMM d, yyyy',
  short: 'MM/dd/yy',
};

/* ----- Types ----- */
export type Props = {
  value: Date;
  label?: string;
  labelVisible?: boolean;
  isOpenedAsDefault?: boolean;
  hideHelpIcon?: boolean;
  dateFormat?: 'standard' | 'short';
  onChange: (date: Date | null, event?: React.SyntheticEvent<any> | undefined) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  testId?: string;
} & Omit<ReactDatePickerProps, 'value'>;

/* ----- Main Component ----- */
const ControlledDatePicker = ({
  value,
  onChange,
  onBlur,
  hideHelpIcon = true,
  label,
  labelVisible = true,
  isOpenedAsDefault = false,
  className = '',
  dateFormat = 'standard',
  testId,
  ...restProps
}: Props) => {
  const { t } = useTranslation();
  const datePickerRef = React.useRef(null);

  React.useEffect(() => {
    datePickerRef.current.setOpen(isOpenedAsDefault);
  }, []);

  const handleDateChange = (dateInput) => {
    onChange(dateInput);
  };

  const inputDateFormat = 'MM/DD/YYYY';
  // used to associate the label with form input for a11y
  const inputId = restProps?.id || restProps?.name;
  const handleDateChangeRaw = (event) => {
    if (event.target.value) {
      const dateMoment = moment(event.target.value, inputDateFormat);
      handleDateChange(dateMoment.isValid() ? dateMoment.toDate() : null);
    }
  };

  return (
    <div onBlur={onBlur} className={className} data-testid={testId}>
      {label && (
        <DatePickerLabel className={!labelVisible ? 'visually-hidden' : ''} htmlFor={inputId}>
          {label}
        </DatePickerLabel>
      )}
      <CalendarIconWrapper>
        <StyledDatePicker
          ref={datePickerRef}
          selected={value}
          onChange={handleDateChange}
          onChangeRaw={handleDateChangeRaw}
          isClearable={!!value}
          dateFormat={DATE_FORMATS[dateFormat || 'standard']}
          id={inputId}
          {...restProps}
        />
        <CalendarIcon
          name="calendar"
          onClick={() => {
            datePickerRef.current.setOpen(!datePickerRef.current.state.open);
          }}
        />
        {hideHelpIcon ? null : (
          <>
            <HelpIcon name="help" id="help-icon" />
            <StyledUncontrolledTooltip placement="right" autohide={false} target="help-icon">
              {t('common:form.tooltips.datepickerFormat')}
            </StyledUncontrolledTooltip>
          </>
        )}
      </CalendarIconWrapper>
    </div>
  );
};

export default ControlledDatePicker;
